import React, { useState, useEffect, useContext, useRef } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AuthContext } from "@/App";
import { toast } from "sonner";
import { User, Mail, Building, Briefcase, Globe, Github, Linkedin, Twitter, Check, Shield, Lock, Trash2, Download, Camera, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function InitialsAvatar({ name, size = 80 }) {
  const initials = (name || "U").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
  return (
    <div className="rounded-full bg-gradient-to-br from-[var(--qn-primary)] to-[var(--qn-secondary)] flex items-center justify-center text-white font-bold" style={{ width: size, height: size, fontSize: size * 0.36 }} data-testid="user-avatar">
      {initials}
    </div>
  );
}

export default function ProfileTab() {
  const { user, setUser } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);

  const isDemo = user?.is_demo;

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API}/profile`, { withCredentials: true });
        setProfile(res.data);
        setForm({
          display_name: res.data.profile?.display_name || res.data.name || "",
          bio: res.data.profile?.bio || "",
          company: res.data.company || "",
          job_title: res.data.profile?.job_title || "",
          timezone: res.data.profile?.timezone || "UTC",
          website: res.data.profile?.website || "",
          github: res.data.profile?.social?.github || "",
          linkedin: res.data.profile?.social?.linkedin || "",
          twitter: res.data.profile?.social?.twitter || "",
        });
      } catch {}
    };
    fetchProfile();
  }, []);

  const handleSave = async () => {
    if (isDemo) { toast.error("Demo users cannot modify profile"); return; }
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/profile`, form, { withCredentials: true });
      setProfile(res.data);
      setUser(prev => ({ ...prev, name: form.display_name, company: form.company }));
      toast.success("Profile updated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  const handlePasswordChange = async () => {
    setPwSaving(true);
    try {
      await axios.post(`${API}/profile/change-password`, pwForm, { withCredentials: true });
      toast.success("Password changed! Please log in again.");
      setPwForm({ current_password: "", new_password: "" });
      setShowPasswordChange(false);
      window.location.href = "/login";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    }
    setPwSaving(false);
  };

  const handleDataExport = async () => {
    const password = window.prompt("Enter your password to confirm data export:");
    if (!password) return;
    try {
      const res = await axios.post(`${API}/account/export`, { password });
      if (res.data.export_id) {
        toast.success("Export ready! Downloading...");
        window.open(`${API}/account/export/${res.data.export_id}/download`, "_blank");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed");
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) {
      toast.error("Only JPEG, PNG, WebP, or GIF images are allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/profile/avatar`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfile(prev => ({ ...prev, picture: res.data.avatar_url }));
      setUser(prev => ({ ...prev, picture: res.data.avatar_url }));
      toast.success("Avatar uploaded!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setUploadingAvatar(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm("This will schedule your account for permanent deletion after 30 days. You can cancel during this period. Continue?")) return;
    const password = window.prompt("Enter your password to confirm:");
    if (!password) return;
    try {
      const res = await axios.post(`${API}/account/delete`, { password });
      toast.success(`Account scheduled for deletion on ${new Date(res.data.deletion_date).toLocaleDateString()}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to schedule deletion");
    }
  };

  const updateField = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  if (!profile) return <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-[var(--qn-primary)] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="profile-tab">
      <h2 className="text-xl font-bold tracking-tight">My Profile</h2>

      {/* Avatar + Name */}
      <Card className="border-[var(--qn-border)] bg-[var(--qn-card)]">
        <CardContent className="p-6">
          <div className="flex items-center gap-5">
            <div className="relative group">
              {profile.picture ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${profile.picture}`} alt="" className="w-20 h-20 rounded-full object-cover" data-testid="user-avatar" />
              ) : (
                <InitialsAvatar name={profile.name || profile.profile?.display_name} />
              )}
              {!isDemo && (
                <>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    data-testid="avatar-upload-trigger"
                  >
                    {uploadingAvatar ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : <Camera className="w-5 h-5 text-white" />}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={handleAvatarUpload}
                    data-testid="avatar-file-input"
                  />
                </>
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold">{profile.name}</h3>
              <div className="flex items-center gap-2 text-sm text-[var(--qn-foreground-muted)]">
                <Mail className="w-3.5 h-3.5" />
                {profile.email}
                {profile.email_verified && <Badge className="text-[9px] bg-emerald-500/10 text-emerald-400 border-emerald-500/20">Verified</Badge>}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="text-[10px]">{profile.role}</Badge>
                <Badge variant="outline" className="text-[10px]">{profile.subscription_tier}</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Form */}
      <Card className="border-[var(--qn-border)] bg-[var(--qn-card)]">
        <CardHeader className="pb-3"><CardTitle className="text-base">Personal Information</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-[var(--qn-foreground-muted)] flex items-center gap-1"><User className="w-3 h-3" /> Display Name</label>
              <Input value={form.display_name} onChange={e => updateField("display_name", e.target.value)} className="mt-1 bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="profile-display-name" />
            </div>
            <div>
              <label className="text-sm text-[var(--qn-foreground-muted)] flex items-center gap-1"><Building className="w-3 h-3" /> Company</label>
              <Input value={form.company} onChange={e => updateField("company", e.target.value)} className="mt-1 bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="profile-company" />
            </div>
            <div>
              <label className="text-sm text-[var(--qn-foreground-muted)] flex items-center gap-1"><Briefcase className="w-3 h-3" /> Job Title</label>
              <Input value={form.job_title} onChange={e => updateField("job_title", e.target.value)} className="mt-1 bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="profile-job-title" />
            </div>
            <div>
              <label className="text-sm text-[var(--qn-foreground-muted)] flex items-center gap-1"><Globe className="w-3 h-3" /> Website</label>
              <Input value={form.website} onChange={e => updateField("website", e.target.value)} placeholder="https://" className="mt-1 bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="profile-website" />
            </div>
          </div>
          <div>
            <label className="text-sm text-[var(--qn-foreground-muted)]">Bio</label>
            <textarea value={form.bio} onChange={e => updateField("bio", e.target.value)} rows={3} className="mt-1 w-full rounded-md bg-[var(--qn-background)] border border-[var(--qn-border)] p-2 text-sm resize-none" maxLength={500} data-testid="profile-bio" />
          </div>

          <div className="text-sm font-medium text-[var(--qn-foreground-muted)] mt-4">Social Links</div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-[var(--qn-foreground-muted)] flex items-center gap-1"><Github className="w-3 h-3" /> GitHub</label>
              <Input value={form.github} onChange={e => updateField("github", e.target.value)} placeholder="username" className="mt-1 bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="profile-github" />
            </div>
            <div>
              <label className="text-xs text-[var(--qn-foreground-muted)] flex items-center gap-1"><Linkedin className="w-3 h-3" /> LinkedIn</label>
              <Input value={form.linkedin} onChange={e => updateField("linkedin", e.target.value)} placeholder="username" className="mt-1 bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="profile-linkedin" />
            </div>
            <div>
              <label className="text-xs text-[var(--qn-foreground-muted)] flex items-center gap-1"><Twitter className="w-3 h-3" /> Twitter</label>
              <Input value={form.twitter} onChange={e => updateField("twitter", e.target.value)} placeholder="username" className="mt-1 bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="profile-twitter" />
            </div>
          </div>

          <Button onClick={handleSave} disabled={saving || isDemo} className="bg-[var(--qn-primary)]" data-testid="save-profile">
            {saving ? "Saving..." : <><Check className="w-4 h-4 mr-1" /> Save Changes</>}
          </Button>
        </CardContent>
      </Card>

      {/* Security Section */}
      <Card className="border-[var(--qn-border)] bg-[var(--qn-card)]">
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Shield className="w-4 h-4" /> Security</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {/* Password Change */}
          {profile.password_hash !== undefined && (
            <div className="flex items-center justify-between p-3 rounded-lg border border-[var(--qn-border)]">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-[var(--qn-foreground-muted)]" />
                <span className="text-sm">Password</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowPasswordChange(!showPasswordChange)} data-testid="change-password-btn">
                Change Password
              </Button>
            </div>
          )}

          {showPasswordChange && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="space-y-3 p-3 rounded-lg border border-[var(--qn-border)]">
              <Input type="password" placeholder="Current password" value={pwForm.current_password} onChange={e => setPwForm(p => ({ ...p, current_password: e.target.value }))} className="bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="current-password" />
              <Input type="password" placeholder="New password (12+ chars, upper, lower, number, special)" value={pwForm.new_password} onChange={e => setPwForm(p => ({ ...p, new_password: e.target.value }))} className="bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="new-password" />
              <Button onClick={handlePasswordChange} disabled={pwSaving} size="sm" className="bg-[var(--qn-primary)]" data-testid="confirm-password-change">
                {pwSaving ? "Changing..." : "Update Password"}
              </Button>
            </motion.div>
          )}
        </CardContent>
      </Card>

      {/* Data & Privacy */}
      <Card className="border-[var(--qn-border)] bg-[var(--qn-card)]">
        <CardHeader className="pb-3"><CardTitle className="text-base">Data & Privacy</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between p-3 rounded-lg border border-[var(--qn-border)]">
            <div>
              <div className="text-sm font-medium">Export Your Data</div>
              <div className="text-xs text-[var(--qn-foreground-muted)]">Download all your QNNEAL data (JSON format)</div>
            </div>
            <Button variant="outline" size="sm" onClick={handleDataExport} data-testid="export-data-btn">
              <Download className="w-3 h-3 mr-1" /> Request Export
            </Button>
          </div>
          {!isDemo && (
            <div className="flex items-center justify-between p-3 rounded-lg border border-red-500/20 bg-red-500/5">
              <div>
                <div className="text-sm font-medium text-red-400">Delete Account</div>
                <div className="text-xs text-[var(--qn-foreground-muted)]">Permanently delete your account and all data</div>
              </div>
              <Button variant="ghost" size="sm" onClick={handleDeleteAccount} className="text-red-400 hover:bg-red-500/10" data-testid="delete-account-btn">
                <Trash2 className="w-3 h-3 mr-1" /> Delete
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
